import {
    Button,
    Card,
    CardActions,
    FormHelperText,
    InputAdornment,
    OutlinedInput,
    TextField,
    Typography,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import styled from "styled-components";
import { ErrorRecord, Manufacturer, OptionsObject, Product } from "../../types";
import Divider from "@mui/material/Divider";
import React, { useContext, useEffect, useRef } from "react";
import { OptionsArray, Color } from "../../types";
import { useGetEmployee } from "../../hooks/employeeHooks";
import { EmployeeContext } from "../../context/EmployeeContext";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { CreateManufacturer, useAddManufacturer } from "../../hooks/manufacturerHooks";
import { queryClient } from "../../hooks/queryClient";
import { useSnackBar } from "../../context/SnackbarContext";
import { ChevronDown, FileText } from "lucide-react";
import { sx as textFieldSx } from "../../utils/util";
import { DatePicker } from "@mui/lab";
import dayjs from "dayjs";

const filter = createFilterOptions<OptionsObject>();

type Props = {
    product: Product;
    manufacturer: OptionsObject | null;
    designer: OptionsObject | null;
    status: OptionsObject | null;
    action: OptionsObject | null;
    material: OptionsObject | null;
    manufacturersOptions: OptionsArray;
    designersOptions: OptionsArray;
    statusesOptions: OptionsArray;
    actionsOptions: OptionsArray;
    materialsOptions: OptionsArray;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleSelectChange: (value: OptionsObject | null, targetField: string, reason?: string) => void;
    handleProductColorChange: (ids: string[]) => void;
    colors: Color[];
    error: boolean;
    errors: ErrorRecord<Product>;
};

export const DetailsForm = (props: Props) => {
    const cardRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        console.log("material opt", props.materialsOptions);
    }, [props.materialsOptions]);

    const { mutate } = useAddManufacturer();
    const { employee } = useContext(EmployeeContext);
    const employeeQuery = useGetEmployee(employee.companyId, employee.id);
    const snackBar = useSnackBar();
    useEffect(() => {
        if (cardRef.current) {
            cardRef.current.scrollTop = 0;
        }
    }, [props.product?.id]);

    useEffect(() => {
        const fetchEmployee = async () => {
            if (!employee) {
                await employeeQuery.refetch();
            }
        };
        void fetchEmployee();
    }, [employee, employeeQuery]);
    const [open, toggleOpen] = React.useState(false);
    const [dialogValue, setDialogValue] = React.useState<Manufacturer>({
        name: "",
        id: "",
        soecoId: "1111",
    });

    const handleClose = () => {
        setDialogValue({
            name: "",
            id: "",
            soecoId: "1111",
        });
        toggleOpen(false);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!dialogValue.name || dialogValue.name.trim() === "") {
            return;
        }
        const manu = {
            name: dialogValue.name,
            soecoId: dialogValue.soecoId,
        };
        if (navigator.onLine) {
            mutate(manu, {
                onSuccess: createdSupplier => {
                    snackBar.showSnackBar("Manufacturer added", "success");
                    const newManu: OptionsObject = {
                        label: manu.name,
                        id: manu.soecoId,
                    };
                    props.handleSelectChange(newManu, "manufacturer");
                },
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                    }
                },
            });
        } else {
            // Optimistically update the new value for manufacturer
            const newManufacturerList: CreateManufacturer[] = [
                ...(queryClient.getQueryData(["manufacturers"]) as CreateManufacturer[]),
                { ...manu, name: manu.name, soecoId: manu.soecoId },
            ];
            queryClient.setQueryData(["manufacturers"], newManufacturerList);

            snackBar.showSnackBar("No internet: Manufacturer will be added when online", "success");
        }
        handleClose();
    };

    return (
        <Container>
            <Card
                ref={cardRef}
                variant="outlined"
                sx={{
                    borderRadius: "30px",
                    overflowY: "auto",
                    maxHeight: "80vh",
                    padding: "5px",
                    width: "100%",
                }}
            >
                <StyledCardActions>
                    <IconActionsWrapper>
                        <FileText />{" "}
                        <Typography
                            fontFamily={"'Poppins', sans-serif"}
                            fontSize={18}
                            color="#8c8c8c"
                            fontWeight={600}
                        >
                            Details
                        </Typography>
                    </IconActionsWrapper>
                </StyledCardActions>
                <Form id="partial-submit-form" autoComplete="off">
                    <TextField
                        label="Name"
                        name="name"
                        variant="outlined"
                        autoFocus
                        onChange={e => props.handleChange(e)}
                        value={props.product.name}
                        error={props.errors.name ? true : false}
                        helperText={props.errors.name}
                        sx={{ ...textFieldSx, marginTop: "20px" }}
                    />
                    {props.manufacturersOptions &&
                    employee.role &&
                    (employee.role === "admin" ||
                        employee.role === "superAdmin" ||
                        employee.role === "master") ? (
                        <>
                            <Autocomplete
                                disablePortal
                                value={props.manufacturer}
                                onChange={(event, newValue, reason) => {
                                    if (typeof newValue === "string") {
                                        toggleOpen(true);
                                        setDialogValue({
                                            name: newValue,
                                            id: "",
                                            soecoId: "1111",
                                        });
                                    } else if (newValue && newValue.inputValue) {
                                        toggleOpen(true);
                                        setDialogValue({
                                            name: newValue.inputValue,
                                            id: "",
                                            soecoId: "1111",
                                        });
                                    } else {
                                        props.handleSelectChange(newValue, "manufacturer", reason);
                                    }
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    if (params.inputValue !== "") {
                                        filtered.push({
                                            inputValue: params.inputValue,
                                            label: `Add "${params.inputValue}"`,
                                            id: "",
                                        });
                                    }
                                    return filtered;
                                }}
                                options={props.manufacturersOptions}
                                getOptionLabel={option => {
                                    if (typeof option === "string") {
                                        return option;
                                    }
                                    if (option.inputValue) {
                                        return option.inputValue;
                                    }
                                    return option.label;
                                }}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                disableClearable={false}
                                renderOption={(props, option) => {
                                    const { ...optionProps } = props;
                                    return <li {...optionProps}>{option.label}</li>;
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Supplier"
                                        variant="outlined"
                                        sx={textFieldSx}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) =>
                                    option.label === value.label
                                }
                                popupIcon={<ChevronDown size={16} />}
                                sx={textFieldSx}
                            />
                            <Dialog open={open} onClose={handleClose}>
                                <form onSubmit={handleSubmit}>
                                    <DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>
                                        Add a new supplier
                                    </DialogTitle>
                                    <DialogContent>
                                        <DialogContentText
                                            style={{ fontFamily: "'Poppins', sans-serif" }}
                                        >
                                            Is a supplier missing? Add it here.
                                        </DialogContentText>
                                        <TextField
                                            margin="dense"
                                            id="name"
                                            value={dialogValue.name}
                                            onChange={event => {
                                                setDialogValue({
                                                    ...dialogValue,
                                                    name: event.target.value,
                                                });
                                            }}
                                            label="Name of supplier"
                                            type="text"
                                            variant="outlined"
                                            sx={textFieldSx}
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleClose}>Cancel</Button>
                                        <Button type="submit">Add</Button>
                                    </DialogActions>
                                </form>
                            </Dialog>
                        </>
                    ) : (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.manufacturersOptions}
                            onChange={(event, value) =>
                                props.handleSelectChange(value, "manufacturer")
                            }
                            renderInput={params => (
                                <TextField variant="outlined" {...params} label="Supplier" />
                            )}
                            value={props.manufacturer}
                            popupIcon={<ChevronDown size={16} />}
                            sx={textFieldSx}
                        />
                    )}
                    {props.designersOptions && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.designersOptions}
                            onChange={(event, value, reason) =>
                                props.handleSelectChange(value, "designer", reason)
                            }
                            renderInput={params => (
                                <TextField variant="outlined" {...params} label="Designer" />
                            )}
                            value={props.designer}
                            popupIcon={<ChevronDown size={16} />}
                            sx={textFieldSx}
                        />
                    )}
                    <Divider />
                    {props.colors && (
                        <div
                            style={{
                                width: "100%",
                                height: "100%",
                                paddingLeft: 4,
                                paddingRight: 4,
                                paddingTop: 8,
                                paddingBottom: 8,
                                borderRadius: 4,
                                outline: "1px #D4D4D4 solid",
                                outlineOffset: "-1px",
                                flexDirection: "row",
                                flexWrap: "wrap", // <-- så de bryts till nästa rad om de inte får plats
                                justifyContent: "flex-start",
                                alignItems: "center",
                                gap: 8,
                                display: "inline-flex",
                            }}
                        >
                            {props.colors.map(colorObj => {
                                const isSelected = props.product.colors?.includes(colorObj.id);
                                return (
                                    <div
                                        key={colorObj.id}
                                        style={{
                                            width: 48,
                                            height: 48,
                                            background: colorObj.hex,
                                            border: isSelected
                                                ? "3px solid black"
                                                : "1px solid transparent",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => {
                                            const currentColors = props.product.colors || [];
                                            let updatedColors;
                                            if (isSelected) {
                                                updatedColors = currentColors.filter(
                                                    c => c !== colorObj.id,
                                                );
                                            } else {
                                                updatedColors = [...currentColors, colorObj.id];
                                            }
                                            props.handleProductColorChange(updatedColors);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    )}
                    <Divider />
                    {props.materialsOptions && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            options={props.materialsOptions}
                            getOptionLabel={option => {
                                if (!option) return "";

                                switch (employeeQuery?.data?.language) {
                                    case "sv":
                                        return option.name || "";
                                    case "en":
                                        return option.nameEN || "";
                                    case "no":
                                        return option.nameNO || "";
                                    case "dk":
                                        return option.nameDK || "";
                                    case "fi":
                                        return option.nameFI || "";
                                    default:
                                        return option.nameEN || option.name || "";
                                }
                            }}
                            onChange={(event, value) => props.handleSelectChange(value, "material")}
                            renderInput={params => (
                                <TextField variant="outlined" {...params} label="Material" />
                            )}
                            value={props.material}
                            popupIcon={<ChevronDown size={16} />}
                            sx={textFieldSx}
                        />
                    )}
                    <Typography
                        fontFamily={"'Poppins', sans-serif"}
                        fontSize={18}
                        color="#8c8c8c"
                        fontWeight={200}
                    >
                        Measurements
                    </Typography>
                    <Divider />
                    <OutlinedInput
                        value={props.product.height}
                        name="height"
                        onChange={e => props.handleChange(e)}
                        placeholder="Height"
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={textFieldSx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Width"
                        name="width"
                        value={props.product.width}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={textFieldSx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Depth"
                        name="depth"
                        value={props.product.depth}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={textFieldSx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Length"
                        name="length"
                        value={props.product.length}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={textFieldSx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />
                    <OutlinedInput
                        placeholder="Seating Height"
                        name="seatHeight"
                        value={props.product.seatHeight}
                        onChange={e => props.handleChange(e)}
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        sx={textFieldSx}
                        endAdornment={
                            <InputAdornment position="end">
                                <Typography style={typographySx}>mm</Typography>
                            </InputAdornment>
                        }
                    />

                    <Divider />
                    {props.statusesOptions && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.statusesOptions}
                            onChange={(event, value) => props.handleSelectChange(value, "status")}
                            renderInput={params => (
                                <TextField
                                    sx={textFieldSx}
                                    variant="outlined"
                                    {...params}
                                    label="Status"
                                />
                            )}
                            popupIcon={<ChevronDown size={16} />}
                            sx={textFieldSx}
                            value={props.status}
                            // getOptionLabel={option => option.label?.toString() || option.label}
                            getOptionLabel={option => {
                                if (!option) return "";

                                switch (employeeQuery?.data?.language) {
                                    case "sv":
                                        return option.nameSV || "";
                                    case "en":
                                        return option.name || "";
                                    case "no":
                                        return option.nameNO || "";
                                    case "dk":
                                        return option.nameDK || "";
                                    case "fi":
                                        return option.nameFI || "";
                                    default:
                                        return option.name || "";
                                }
                            }}
                        />
                    )}
                    {props.actionsOptions && (
                        <Autocomplete
                            disablePortal
                            isOptionEqualToValue={(option, value) => option.label === value.label}
                            options={props.actionsOptions}
                            onChange={(event, value) => props.handleSelectChange(value, "action")}
                            renderInput={params => (
                                <TextField
                                    sx={textFieldSx}
                                    variant="outlined"
                                    {...params}
                                    label="Action"
                                />
                            )}
                            value={props.action}
                            popupIcon={<ChevronDown size={16} />}
                            sx={textFieldSx}
                            getOptionLabel={option => {
                                if (!option) return "";

                                switch (employeeQuery?.data?.language) {
                                    case "sv":
                                        return option.nameSV || "";
                                    case "en":
                                        return option.name || "";
                                    case "no":
                                        return option.nameNO || "";
                                    case "dk":
                                        return option.nameDK || "";
                                    case "fi":
                                        return option.nameFI || "";
                                    default:
                                        return option.name || "";
                                }
                            }}
                        />
                    )}
                    <Divider />
                    {/* <TextField
                        label="Address"
                        name="address"
                        variant="outlined"
                        // autoFocus
                        onChange={e => props.handleChange(e)}
                        value={props.product.address}
                        error={props.errors.address ? true : false}
                        // helperText={}
                        sx={textFieldSx}
                    /> */}
                    {/* <TextField
                        label="Avalible"
                        name="avalible"
                        variant="outlined"
                        // autoFocus
                        onChange={e => props.handleChange(e)}
                        value={props.product.avalible}
                        error={props.errors.avalible ? true : false}
                        helperText={props.errors.avalible}
                        sx={textFieldSx}
                    /> */}
                    <DatePicker
                        label="Available from date"
                        value={props.product.avalible ? dayjs(props.product.avalible) : null}
                        onChange={newValue => {
                            props.handleChange({
                                target: {
                                    name: "avalible",
                                    value: newValue ? dayjs(newValue).format("YYYY-MM-DD") : "",
                                },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                // sx={sx}
                                error={!!props.errors.avalible}
                                helperText={props.errors.avalible}
                            />
                        )}
                    />

                    <TextField
                        label="Comment"
                        name="comment"
                        sx={{ ...textFieldSx, marginBottom: "20px" }}
                        variant="outlined"
                        value={props.product.comment}
                        onChange={e => props.handleChange(e)}
                    />
                </Form>
            </Card>
        </Container>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    margin: 0 auto;
    gap: 20px;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: 5px;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center; /* Centrerar vertikalt i flex-container */
`;
const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    padding: 0 10px;
`;

const typographySx = {
    fontSize: "16px",
    fontFamily: "'Yllw Sans a1 241219', sans-serif",
    fontWeight: "400",
    color: "gray",
};
